import apiClient from '@u/apiClient'
import { contentDispositionFilename } from '@u/http/contentDispositionFilename'
import GetReportController from '@/wayfinder/actions/App/Http/Controllers/Report/GetReportController'
import GetReportRowsController from '@/wayfinder/actions/App/Http/Controllers/Report/GetReportRowsController'
import ExportReportController from '@/wayfinder/actions/App/Http/Controllers/Report/ExportReportController'
import ConvertReportToDashboardController from '@/wayfinder/actions/App/Http/Controllers/Report/ConvertReportToDashboardController'
import type { ReportRows } from '@c/report/reportGrid'

export type Report = {
  id: string
  title: string
  entity: string
  definition: Record<string, unknown>
  source: 'assistant' | 'manual'
  conversation_id: string | null
  created_at: string
  updated_at: string
  // The recipe's filters written out, when the API sends them
  filters_summary?: string[]
}

// The report page shows its own message for these: no toast on top
const PAGE_HANDLES_ERRORS = {
  skipForbiddenToast: true,
  skipNotFoundToast: true,
  skipValidationToast: true
}

export default {
  // The saved recipe; 404 when it belongs to another user
  fetchReport(_context, id: string): Promise<Report> {
    return apiClient
      .route(GetReportController.get({ report: id }), PAGE_HANDLES_ERRORS)
      .then((response) => response.data.data)
  },
  // Every row up to the API's limit: the recipe runs on each call, so this
  // can take several seconds on a large report
  fetchReportRows(_context, id: string): Promise<ReportRows> {
    return apiClient
      .route(GetReportRowsController.get({ report: id }), PAGE_HANDLES_ERRORS)
      .then((response) => response.data)
  },
  // A dossier dashboard with the report's name, columns, filters and first
  // sort, the user subscribed to it; resolves with the dashboard's id
  convertReportToDashboard(_context, id: string): Promise<number> {
    return apiClient
      .route(
        ConvertReportToDashboardController.post({ report: id }),
        {},
        PAGE_HANDLES_ERRORS
      )
      .then((response) => response.data.template_dashboard.id)
  },
  // The .xlsx built by the API, named after the report's title
  exportReport(
    _context,
    id: string
  ): Promise<{ blob: Blob; filename: string }> {
    return apiClient
      .route(ExportReportController.get({ report: id }), {
        ...PAGE_HANDLES_ERRORS,
        responseType: 'blob'
      })
      .then((response) => ({
        blob: response.data,
        filename: contentDispositionFilename(
          response.headers.get('content-disposition'),
          `report-${id}.xlsx`
        )
      }))
  }
}
